import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner
} from 'reactstrap';
import { Staff } from '../types';
import StaffService from '../services/staffService';
import AuthService from '../services/authService';
import api from '../services/api';
// CSS profile modal đã được hợp nhất vào App.css

interface ProfileModalProps {
  isOpen: boolean;
  toggle: () => void;
  onProfileUpdate: () => void;
}

// Các hằng số cho việc xác thực mật khẩu
const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_MAX_LENGTH = 20;

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, toggle, onProfileUpdate }) => {
  const [user, setUser] = useState<Staff | null>(null);
  const [fullName, setFullName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    setUser(currentUser);
    if (currentUser) {
      setFullName(currentUser.fullName || '');
    }
    // Reset form mỗi khi mở modal
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setChangePassword(false);
    setPasswordErrors([]);
  };

  // Xác thực mật khẩu và trả về danh sách lỗi
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < PASSWORD_MIN_LENGTH) {
      errors.push(`Mật khẩu phải có ít nhất ${PASSWORD_MIN_LENGTH} ký tự`);
    }
    
    if (password.length > PASSWORD_MAX_LENGTH) {
      errors.push(`Mật khẩu không được vượt quá ${PASSWORD_MAX_LENGTH} ký tự`);
    }
    
    // Thêm các quy tắc xác thực khác nếu cần
    // Ví dụ: kiểm tra mật khẩu có chứa ký tự đặc biệt, số, chữ hoa, chữ thường, ...
    
    return errors;
  };

  // Hàm chỉ cập nhật thông tin cơ bản (không đổi mật khẩu)
  const updateBasicInfo = async () => {
    if (!user || !user.id) {
      throw new Error('Không tìm thấy thông tin người dùng');
    }

    try {
      // Chỉ gửi thông tin cơ bản cần cập nhật
      const updateData = {
        fullName: fullName
      };
      
      console.log('Cập nhật thông tin cơ bản:', updateData);
      const response = await StaffService.update(user.id, updateData as Staff);
      
      // Cập nhật thông tin người dùng trong localStorage
      const updatedUser = { ...user, fullName: fullName };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return response;
    } catch (error) {
      console.error('Lỗi cập nhật thông tin cơ bản:', error);
      throw error;
    }
  };

  // Cập nhật mật khẩu qua API PUT /staff/:id
  const updatePasswordFallback = async () => {
    if (!user || !user.id) {
      throw new Error('Không tìm thấy thông tin người dùng');
    }

    try {
      // Sử dụng endpoint PUT /staff/:id với dữ liệu tối thiểu
      const updateData: any = {
        fullName: fullName, // Giữ thông tin hiện tại
        password: newPassword,
        oldPassword: currentPassword
      };
      
      console.log('Cập nhật mật khẩu (phương pháp dự phòng):', { id: user.id });
      const response = await StaffService.update(user.id, updateData);
      return response;
    } catch (error) {
      console.error('Lỗi cập nhật mật khẩu (phương pháp dự phòng):', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setPasswordErrors([]);
    setIsLoading(true);
    
    // Kiểm tra dữ liệu nhập vào
    if (!fullName.trim()) {
      setError('Họ tên không được để trống');
      setIsLoading(false);
      return;
    }

    if (changePassword) {
      if (!currentPassword) {
        setError('Vui lòng nhập mật khẩu hiện tại');
        setIsLoading(false);
        return;
      }
      
      if (!newPassword) {
        setError('Vui lòng nhập mật khẩu mới');
        setIsLoading(false);
        return;
      }
      
      if (newPassword !== confirmPassword) {
        setError('Mật khẩu mới và xác nhận mật khẩu không khớp');
        setIsLoading(false);
        return;
      }
      
      // Xác thực mật khẩu mới
      const errors = validatePassword(newPassword);
      if (errors.length > 0) {
        setPasswordErrors(errors);
        setIsLoading(false);
        return;
      }
    }

    if (!user || !user.id) {
      setError('Không tìm thấy thông tin người dùng');
      setIsLoading(false);
      return;
    }

    try {
      // Nếu chỉ cập nhật thông tin cơ bản
      if (!changePassword) {
        await updateBasicInfo();
        setSuccess('Cập nhật thông tin thành công');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          resetForm();
          onProfileUpdate();
          toggle();
        }, 1500);
        setIsLoading(false);
        return;
      }
      
      // Nếu cần đổi mật khẩu, thử phương pháp dự phòng (PUT /staff/:id)
      try {
        await updatePasswordFallback();
        setSuccess('Cập nhật thông tin và mật khẩu thành công');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          resetForm();
          onProfileUpdate();
          toggle();
        }, 1500);
        setIsLoading(false);
      } catch (passwordError: any) {
        console.error('Chi tiết lỗi cập nhật mật khẩu:', passwordError);
        
        // Xử lý lỗi Sequelize validation
        if (passwordError.response && 
            passwordError.response.data && 
            passwordError.response.data.message && 
            passwordError.response.data.message.includes('Validation len on password failed')) {
          setError(`Mật khẩu mới phải có độ dài từ ${PASSWORD_MIN_LENGTH} đến ${PASSWORD_MAX_LENGTH} ký tự`);
        } 
        // Kiểm tra lỗi mật khẩu cũ không đúng
        else if (passwordError.response && 
                passwordError.response.data && 
                passwordError.response.data.message && 
                passwordError.response.data.message.includes('Mật khẩu hiện tại không đúng')) {
          setError('Mật khẩu hiện tại không đúng');
        }
        // Các lỗi khác
        else if (passwordError.response && passwordError.response.data && passwordError.response.data.message) {
          setError(passwordError.response.data.message);
        } else if (passwordError.response && passwordError.response.status === 401) {
          setError('Mật khẩu hiện tại không đúng');
        } else if (passwordError.response && passwordError.response.status === 500) {
          setError('Lỗi server: Không thể cập nhật mật khẩu');
        } else {
          setError('Không thể cập nhật mật khẩu: ' + (passwordError.message || 'Lỗi không xác định'));
        }
        
        // Tắt trạng thái loading khi có lỗi đổi mật khẩu
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('Chi tiết lỗi cập nhật:', err);
      let errorMessage = 'Đã xảy ra lỗi khi cập nhật thông tin';
      
      if (err.response) {
        // Có response từ server
        console.log('Response status:', err.response.status);
        console.log('Response data:', err.response.data);
        
        if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.status === 401) {
          errorMessage = 'Không có quyền cập nhật thông tin này';
        } else if (err.response.status === 500) {
          errorMessage = 'Lỗi server: Vui lòng liên hệ quản trị viên';
        }
      } else if (err.request) {
        // Request được gửi nhưng không nhận được response
        errorMessage = 'Không thể kết nối đến server';
      } else {
        // Lỗi khi thiết lập request
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      
      // Tắt trạng thái loading khi có lỗi cập nhật khác
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} className="modal-user-profile">
      <ModalHeader toggle={handleClose}>Thông tin tài khoản</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {showSuccess && (
          <Alert color="success" className="fade-in">
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}
        {passwordErrors.length > 0 && (
          <Alert color="warning">
            <strong>Yêu cầu mật khẩu:</strong>
            <ul className="mb-0 mt-1">
              {passwordErrors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="username">Tên đăng nhập</Label>
            <Input
              type="text"
              id="username"
              value={user?.username || ''}
              disabled
            />
          </FormGroup>
          
          <FormGroup>
            <Label for="fullName">Họ và tên</Label>
            <Input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Nhập họ và tên"
            />
          </FormGroup>
          
          <FormGroup>
            <Label for="role">Vai trò</Label>
            <Input
              type="text"
              id="role"
              value={user?.role === 'admin' ? 'Quản trị viên' : 'Nhân viên'}
              disabled
            />
          </FormGroup>
          
          <FormGroup check className="mb-3">
            <Input
              type="checkbox"
              id="changePasswordCheck"
              checked={changePassword}
              onChange={() => setChangePassword(!changePassword)}
            />
            <Label check for="changePasswordCheck">Đổi mật khẩu</Label>
          </FormGroup>
          
          {changePassword && (
            <>
              <FormGroup>
                <Label for="currentPassword">Mật khẩu hiện tại</Label>
                <Input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Nhập mật khẩu hiện tại"
                />
              </FormGroup>
              
              <FormGroup>
                <Label for="newPassword">Mật khẩu mới</Label>
                <Input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    // Xóa lỗi mật khẩu khi người dùng nhập lại
                    if (passwordErrors.length > 0) {
                      setPasswordErrors([]);
                    }
                  }}
                  placeholder="Nhập mật khẩu mới"
                />
                <small className="form-text text-muted">
                  Mật khẩu phải có ít nhất {PASSWORD_MIN_LENGTH} ký tự
                </small>
              </FormGroup>
              
              <FormGroup>
                <Label for="confirmPassword">Xác nhận mật khẩu</Label>
                <Input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Nhập lại mật khẩu mới"
                />
              </FormGroup>
            </>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        
        <Button color="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Đang xử lý...
            </>
          ) : (
            'Lưu thay đổi'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ProfileModal; 